import type { ObservaClient } from "@morganisbatman/observa-client-react";

// Module-scope seam so non-React code (the tRPC + auth fetch wrappers)
// can record errors to observa. trpc.ts and auth-client.ts run at module
// scope and can't call useObserva(); ObservaWiring (app/_layout.tsx)
// registers the live client here once the provider mounts. Calls made
// before registration are no-ops rather than throwing.
let client: ObservaClient | null = null;

export function registerObservaClient(c: ObservaClient): void {
  client = c;
}

// Statuses that signal a transport/routing fault worth alarming on. We
// deliberately skip the normal 4xx auth states (401/403): an anonymous
// client polling a protected procedure is expected, not an outage. A 404
// means the request never reached a route (the 2026-06 prod outage:
// EXPO_PUBLIC_API_URL carried a stray /trpc, so every call hit
// /trpc/trpc/* and 404'd); 5xx means the server itself broke.
function isTransportFault(status: number): boolean {
  return status === 404 || status >= 500;
}

/**
 * Record a backend transport failure as an observa.error.
 *
 * Without this, tRPC/auth failures are invisible to observa: a non-ok
 * Response is handed back to tRPC, which throws "Unable to transform
 * response from server" downstream where nothing logs it — so the
 * 2026-06 prod outage (every request 404ing) produced zero error events
 * and no alert fired. This seam makes the app's own backend failures
 * observable so an absence/threshold alert can catch the next one.
 *
 * `url` is reduced to its path (query stripped) before it reaches the
 * error message: query strings carry tRPC input payloads, which would
 * both leak data and fragment error grouping per-input.
 */
export function captureBackendError(
  url: string,
  detail: { status?: number; statusText?: string; cause?: unknown },
): void {
  if (client === null) return;
  const { status, statusText, cause } = detail;
  if (status !== undefined && !isTransportFault(status)) return;

  const path = url.split("?")[0] ?? url;
  const message =
    status !== undefined
      ? `Backend request failed: HTTP ${status}${
          statusText ? ` ${statusText}` : ""
        } — ${path}`
      : `Backend request failed (network): ${path}`;

  void client.error({
    error: {
      name: "BackendRequestError",
      message,
      stack: cause instanceof Error ? cause.stack : undefined,
    },
    context: { path, status: status ?? null, transport: "fetch" },
  });
}
